/**
 * FormShowcase — antd 폼 컴포넌트(Form/Input/Select/DatePicker 등) 카탈로그.
 * (상세 JSDoc 헤더는 본 파일 하단 export default 함수 위 참조)
 */
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Slider,
  Radio,
  Checkbox,
  Button,
  InputNumber,
  AutoComplete,
  Cascader,
  Mentions,
  TreeSelect,
  Transfer,
  Upload,
  Rate,
  ColorPicker,
  TimePicker,
  Space,
  Typography,
  Row,
  Col
} from 'antd'
import type { TransferProps } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useState } from 'react'
import PageContainer from '@/components/common/PageContainer'

/**
 * ============================================================================
 * FormShowcase — antd Form 입력 컴포넌트 카탈로그
 * ============================================================================
 *
 * 【역할】 antd Form 의 주요 입력 컨트롤 ~20종을 4개 카드 영역으로 분류 시연.
 *
 * 【시연 항목】
 *   - 기본 입력 — Input/InputNumber/Radio/Select/DatePicker/TimePicker.RangePicker/Switch/Checkbox
 *   - 고급 선택 — AutoComplete/Cascader/TreeSelect/Mentions/Upload(폼 내부)
 *   - Rate/Slider/ColorPicker — 평가/슬라이더/색상
 *   - Transfer — 좌우 이동 리스트
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/form' }` (lazy)
 * 【포팅 원본】 자체 작성 (Wave 16 컴포넌트 카탈로그)
 * 【의존성】 antd Form 전체 입력 컨트롤 + useLabel(t)
 * 【참고】 https://ant.design/components/form
 */
import { useLabel } from '@/framework/useLabel'

const { Title } = Typography

const cascaderOptions = [
  {
    value: 'seoul',
    label: '서울',
    children: [
      { value: 'gangnam', label: '강남구' },
      { value: 'songpa', label: '송파구' }
    ]
  },
  {
    value: 'busan',
    label: '부산',
    children: [{ value: 'haeundae', label: '해운대구' }]
  }
]

const treeSelectData = [
  {
    title: '개발',
    value: 'dev',
    children: [
      { title: '프론트엔드', value: 'fe' },
      { title: '백엔드', value: 'be' }
    ]
  },
  { title: '기획', value: 'plan' },
  { title: '디자인', value: 'design' }
]

const transferData = Array.from({ length: 10 }).map((_, i) => ({
  key: `item-${i}`,
  title: `항목 ${i + 1}`,
  description: `설명 ${i + 1}`
}))

// 【왜 module-scope?】 매 렌더 새 객체 생성 시 Card styles prop reference 변경 → 자식 재계산
//   동일 reference 보장으로 antd Card 의 내부 memoization 효과 유지
// 【대안】 useMemo 로 컴포넌트 내부에 두면 deps=[] 로 같은 효과 가능
//   하지만 module-scope 가 더 명시적이고 import 시 한 번만 평가됨 (성능 미세 우위)
// 【overflow:auto】 카탈로그 셀이 가변 콘텐츠 — 일부 위젯(Transfer)이 넘칠 때 스크롤 표시
const cardBody = { body: { padding: 10, height: '100%', overflow: 'auto' } }

// ─────────────────────────────────────────────────────────
// 컴포넌트 본체
// ─────────────────────────────────────────────────────────
// 4개 카드 = 기본 입력(폼 1개) / 고급 선택(5개 위젯) / Rate-Slider-Color / Transfer.
// Form.useForm() 인스턴스는 '기본 입력' 카드에서만 사용.
// 다른 카드는 위젯 단독 시연 — 폼 통합 없이 controlled/uncontrolled 자유롭게.
//
// 【antd Form 핵심 개념】
//   - Form.useForm() → form 인스턴스 생성 (외부에서 메서드 호출 가능)
//   - Form.Item name='X' → 자식 입력 컴포넌트가 form.values.X 와 자동 양방향 바인딩
//   - rules → 검증 규칙 배열 (required, max, pattern, validator 함수 등)
//   - onFinish → 검증 통과 + 제출 시 호출 (검증 실패 시 onFinishFailed 분기)
/**
 * FormShowcase — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function FormShowcase() {
  const { t } = useLabel()
  // ─── Form 인스턴스 — useForm() ─
  // 【왜 useForm?】
  //   - 외부에서 form.setFieldsValue/getFieldsValue/validateFields 호출 가능
  //   - 입력 컴포넌트 trigger='onChange' + valuePropName 자동 와이어링
  //   - 컨트롤드 vs 언컨트롤드 혼용 시 단일 진실원천(SoT) 제공
  // 【언제 일반 useState?】 폼 필드 1~2개 + validation 불필요할 때
  const [form] = Form.useForm()
  // Transfer 의 우측 선택 키 — controlled 방식 (antd Transfer 권장 패턴)
  const [transferKeys, setTransferKeys] = useState<string[]>([])

  /**
   * 기본 폼 제출 핸들러 — 데모용 console 출력만 수행.
   * @param values — Form.useForm() 으로 수집한 모든 필드 값
   */
  // 【실제 구현 시】
  //   1) const res = await transaction({ service: '...', inds: { ds_form: values } })
  //   2) res.success → form.resetFields() + 성공 토스트
  //   3) res.success=false → 에러 토스트 (서버 검증 메시지)
  // 【unknown vs any】
  //   unknown: 사용 전 타입 가드 강제 (안전)
  //   any: 어떤 연산이든 허용 (위험)
  //   여기는 데모라 console.log 만 → unknown 으로 충분
  const onFinish = (values: unknown) => {
    // eslint-disable-next-line no-console
    console.log('submitted', values)
  }

  /**
   * Transfer 의 우측(target) key 목록 변경 콜백.
   * @param nextTargetKeys — antd Transfer 가 넘겨주는 다음 target key 배열
   */
  const onTransferChange: TransferProps['onChange'] = (nextTargetKeys) => {
    setTransferKeys(nextTargetKeys as string[])
  }

  // ─── 렌더 ─
  // PageContainer overflow=hidden 으로 페이지 자체 스크롤 억제
  // 헤더(Title, 고정) + 그리드(4 cells, 잔여 공간) 2단 flex
  // 그리드 셀 내부 overflow=auto 로 카드별 독립 스크롤 가능
  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          flex: '0 0 auto'
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_FORM_COMPONENTS', 'Form Components')}
        </Title>
      </div>

      {/* ─── 12-Col Grid Areas — 폼 카탈로그 비대칭 레이아웃 ─
          basic(5col, 2row span) — 기본 입력 7종이 가장 많아 가장 큰 영역
          adv(4col, 2row span) — 고급 입력 5종
          rate(3col, 1row), xfer(3col, 1row) — 우측 컬럼 상하 분할
          【왜 12 col 시스템?】 antd/bootstrap 표준 — 다양한 비율(1/12, 1/6, 1/4, 1/3...) 표현 가능
          【주의】 영역명이 같은 셀(예: basic 5x2) 은 자동 병합 — 영역간 빈 셀 없도록 정확히 채워야 함 */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas: `
            "basic basic basic basic basic adv adv adv adv rate rate rate"
            "basic basic basic basic basic adv adv adv adv xfer xfer xfer"
          `,
          gap: 10
        }}
      >
        <Card
          size="small"
          title={t('LBL_DEMO_BASIC_INPUT', '기본 입력')}
          styles={cardBody}
          style={{ gridArea: 'basic', display: 'flex', flexDirection: 'column' }}
        >
          {/* ─── 기본 입력 폼 ─
              【왜 layout='horizontal' + labelCol/wrapperCol?】
                좁은 카드 폭에서 vertical 레이아웃은 세로로 길어져 2열 카탈로그 깨짐
                horizontal + 6:18 비율이 라벨 가독성 + 입력영역 폭 균형
              【주의】 valuePropName='checked' 는 Switch/Checkbox 등 boolean 컴포넌트에 필수
                값을 'value' 가 아닌 'checked' 로 받기 때문 (누락 시 controlled 동작 깨짐) */}
          <Form
            form={form}
            layout="horizontal"
            onFinish={onFinish}
            size="small"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            {/* name='name' — Form.useForm 의 values 객체 키
                rules=[{required:true}] — 제출 시 자동 검증 + 라벨 옆 빨간 별 표시 */}
            <Form.Item
              name="name"
              label={t('LBL_NAME', '이름')}
              rules={[{ required: true }]}
              style={{ marginBottom: 8 }}
            >
              <Input placeholder={t('LBL_NAME', '이름')} />
            </Form.Item>
            {/* InputNumber min/max — 키보드 입력 + 스피너 모두 범위 강제 */}
            <Form.Item name="age" label={t('LBL_AGE', '나이')} style={{ marginBottom: 8 }}>
              <InputNumber min={0} max={120} />
            </Form.Item>
            {/* Radio.Group — 단일 선택 (3~5개 옵션 권장, 그 이상은 Select) */}
            <Form.Item name="gender" label={t('LBL_GENDER', '성별')} style={{ marginBottom: 8 }}>
              <Radio.Group>
                <Radio value="M">{t('LBL_MALE', 'M')}</Radio>
                <Radio value="F">{t('LBL_FEMALE', 'F')}</Radio>
              </Radio.Group>
            </Form.Item>
            {/* Select — 옵션 3개 이상 권장 (그 미만은 Radio 가 가시성↑) */}
            <Form.Item name="role" label={t('LBL_ROLE', '역할')} style={{ marginBottom: 8 }}>
              <Select
                options={[
                  { value: 'dev', label: t('LBL_ROLE_DEV', 'Developer') },
                  { value: 'pm', label: 'PM' },
                  { value: 'designer', label: t('LBL_ROLE_DESIGNER', 'Designer') }
                ]}
              />
            </Form.Item>
            {/* DatePicker — Day.js 객체 반환 (antd v5+) — 서버 전송 시 .format() 호출 필요 */}
            <Form.Item name="birth" label={t('LBL_BIRTH', '생년월일')} style={{ marginBottom: 8 }}>
              <DatePicker />
            </Form.Item>
            {/* TimePicker.RangePicker — [start, end] 튜플 반환 */}
            <Form.Item
              name="worktime"
              label={t('LBL_WORKTIME', '근무시간')}
              style={{ marginBottom: 8 }}
            >
              <TimePicker.RangePicker />
            </Form.Item>
            {/* valuePropName='checked' — Switch/Checkbox 의 boolean prop 명이 'checked' 라서 명시 필수
                기본값('value')으로 두면 antd Form 이 setFieldsValue 시 동작 깨짐 */}
            <Form.Item
              name="notify"
              label={t('LBL_NOTIFICATION', '알림')}
              valuePropName="checked"
              style={{ marginBottom: 8 }}
            >
              <Switch />
            </Form.Item>
            {/* offset=6 — labelCol 만큼 좌측 들여쓰기 → 입력 영역과 정렬
                noStyle — Form.Item 의 라벨/마진 제거, Checkbox 단독 표시
                htmlType='submit' — 폼 제출 트리거 (onFinish 호출) */}
            <Row>
              <Col offset={6} span={18}>
                <Space>
                  <Form.Item name="agree" valuePropName="checked" noStyle>
                    <Checkbox>{t('LBL_AGREE_TERMS', '약관 동의')}</Checkbox>
                  </Form.Item>
                  <Button type="primary" htmlType="submit" size="small">
                    {t('BTN_SUBMIT', '제출')}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* ─── 고급 선택 입력 카드 ─
            AutoComplete / Cascader / TreeSelect / Mentions / Upload
            【AutoComplete vs Select】
              - AutoComplete: 사용자 입력값을 옵션 외에도 그대로 사용 가능 (이메일, 검색어)
              - Select: 정해진 옵션만 선택 가능 (역할, 상태)
            【Cascader vs TreeSelect】
              - Cascader: 단일 leaf 선택, 단계별 컬럼 UI (지역 1단계→2단계)
              - TreeSelect: 다중 선택 + 임의 노드 선택 가능 (부서, 권한)
            【Mentions】 텍스트 안에 @user 형식 — 댓글/메시지 입력 전용
            【주의】 Cascader 의 options 트리 깊이가 깊어지면 모바일 UX 저하 — 4단계 이내 권장 */}
        <Card
          size="small"
          title={t('LBL_DEMO_ADVANCED_INPUT', '고급 선택 입력')}
          styles={cardBody}
          style={{ gridArea: 'adv', display: 'flex', flexDirection: 'column' }}
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {/* AutoComplete — filterOption=true 면 입력값으로 옵션 필터
                옵션에 없는 자유 입력값도 그대로 사용 가능 (Select 와 핵심 차이) */}
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>AutoComplete</div>
              <AutoComplete
                size="small"
                style={{ width: '100%' }}
                options={[
                  { value: 'alice@example.com' },
                  { value: 'bob@example.com' },
                  { value: 'carol@example.com' }
                ]}
                placeholder={t('PH_EMAIL_AUTOCOMPLETE', '이메일 자동완성')}
                filterOption
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Cascader</div>
              <Cascader
                size="small"
                options={cascaderOptions}
                placeholder={t('PH_REGION_SELECT', '시/도 선택')}
                style={{ width: '100%' }}
              />
            </div>
            {/* TreeSelect — treeCheckable=true 면 다중 선택 모드
                showCheckedStrategy=SHOW_PARENT — 부모 선택 시 자식은 표시 생략 (UI 간소화) */}
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>TreeSelect</div>
              <TreeSelect
                size="small"
                style={{ width: '100%' }}
                treeData={treeSelectData}
                treeCheckable
                placeholder={t('PH_DEPT_SELECT', '부서 선택')}
                showCheckedStrategy={TreeSelect.SHOW_PARENT}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Mentions</div>
              <Mentions
                style={{ width: '100%' }}
                placeholder={t('PH_MENTIONS', '@ 입력으로 멘션')}
                rows={2}
                options={[
                  { value: 'alice', label: 'alice' },
                  { value: 'bob', label: 'bob' },
                  { value: 'carol', label: 'carol' }
                ]}
              />
            </div>
            {/* Upload — beforeUpload return false → 자동 업로드 비활성 (수동 제출 패턴)
                실 서버 업로드는 onChange + axios/fetch 직접 호출 또는 action prop 사용 */}
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Upload (폼 내부)</div>
              <Upload beforeUpload={() => false} maxCount={3}>
                <Button size="small" icon={<UploadOutlined />}>
                  {t('BTN_SELECT_FILE_MAX3', '파일 선택 (최대 3개)')}
                </Button>
              </Upload>
            </div>
          </Space>
        </Card>

        {/* ─── Rate / Slider / ColorPicker 카드 ─
            【Rate】
              - allowHalf: 0.5 단위 평가 (4.5점) — 더 정밀한 평가가 필요할 땐 Slider 가 적합
              - count 기본 5 — 1~10 범위 조정 가능
            【Slider 단일 vs range】
              - 단일: 임계값/볼륨 등 한 점 선택
              - range: 가격대/날짜 범위 (DatePicker.RangePicker 와 비교 — 숫자 범위면 Slider, 날짜면 DatePicker)
            【ColorPicker showText】 HEX 값 표시 → 디자이너/개발자 협업 시 유용
              showText=false 면 색상 원만 표시 (간소한 UI) */}
        <Card
          size="small"
          title="Rate / Slider / Color"
          styles={cardBody}
          style={{ gridArea: 'rate', display: 'flex', flexDirection: 'column' }}
        >
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Rate</div>
              <Rate allowHalf defaultValue={3.5} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Slider</div>
              <Slider defaultValue={40} style={{ margin: '4px 0' }} />
              <Slider range defaultValue={[20, 70]} style={{ margin: '4px 0' }} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>ColorPicker</div>
              <Space>
                <ColorPicker defaultValue="#1677ff" size="small" />
                <ColorPicker defaultValue="#52c41a" showText size="small" />
              </Space>
            </div>
          </Space>
        </Card>

        {/* ─── Transfer 카드 ─
            【언제 Transfer?】
              - 좌(전체) → 우(선택) 양방향 이동이 명시적 — 사용자가 "선택" 행위를 인지
              - Multi-Select(Select mode='multiple') 와 비교 시: 선택 결과 가시성↑, 선택 수량 많을 때 유리
            【주의】 controlled (targetKeys + onChange) 패턴 — uncontrolled 모드 없음
                  대량 데이터(>1000) 시 자체 가상화 없으므로 listStyle.height 제한 필수 */}
        <Card
          size="small"
          title="Transfer"
          styles={cardBody}
          style={{ gridArea: 'xfer', display: 'flex', flexDirection: 'column' }}
        >
          {/* Transfer — render: 좌/우 리스트 항목 표시 함수
              titles: [좌측 헤더, 우측 헤더] 라벨
              listStyle: 양쪽 리스트 width/height 강제 (좁은 카드 폭에 맞춤) */}
          <Transfer
            dataSource={transferData}
            targetKeys={transferKeys}
            onChange={onTransferChange}
            render={(item) => item.title}
            titles={[t('PH_ALL', '전체'), t('BTN_SELECT', '선택')]}
            listStyle={{ width: 140, height: 180 }}
          />
        </Card>
      </div>
    </PageContainer>
  )
}
